import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Card, Dropdown, List} from 'tea-component';
import SvgIcon from '@home/components/SvgIcon';
import {useMemoizedFn, useRequest} from 'ahooks';
import {useIntl, useParams} from 'umi';
import { calculateDashboardChart, previewDashboardChartSql } from '@dbl/services/dashboard';
import type { IDashboardChartRequest } from '@dbl/services/dashboard';
import {getTrendDateInfo} from '@/pages/datalab/utils/utils';
import Abnormal from '@home/components/Abnormal';
import {SYSTEM_NAME} from '@dbl/core/global';
import NumberTrendChart from "@customComp/NumberTrendChart";
import {
  getScopeFilters,
} from "@/pages/datalab/components/DashboardLayout/components/DragShell/_utils";
import classNames from "classnames";
import './index.scss';
import {handleExportClick} from "@/pages/datalab/components/DashboardLayout/_utils";
import {formatDisplayValue} from "@customComp/Chart/_config/_utils";
import { getOldChartDisplayConfig } from '@dbl/core/chartEditor';
import { ChartType } from '@dbl/core/chart';
import ShowFilterContentIcon, {
  FILTER_CONTENT_KEY
} from "@/pages/datalab/components/ChartCardIconButtons/ShowFilterContentIcon";
import {useChartCardError} from '@dbl/components/DashboardLayout/components/DragShell/hooks/useChartCardError';
import { usePreviewSQL, PreviewSQLModal } from '@dbl/domainComponents/dataSource';

const NumberTrendTragCard = (props: any) => {
  const {
    deleteCard,
    handleOnSubmit,
    dataMsg,
    filterValues,
    userFilter,
    isEdit = true,
    hasChartPrivilege,
    system = 'datalab',
    chartExplore,
    // onForceClose,
    chartsInfoLoading,
    isFilterInit, // 是否为 页面初始进入状态(没有点击confirm, 或者点击了reset)
  } = props;
  const {task_id: taskId} = dataMsg || {};
  const [open, setOpen] = useState(false);
  const cardRootRef = useRef<HTMLDivElement>(null);
  // const [forceVisible, setForceVisible] = useState(!!onForceClose);
  // 经分承接页gameCode
  const {gamecode}: any = useParams();
  const {formatMessage} = useIntl();

  const buildChartParams = useMemoizedFn((queries?: Record<string, unknown>): IDashboardChartRequest => ({
    game_code: gamecode,
    task_id: [String(taskId)],
    queries: {
      filters: Object.values(filterValues || []),
      user_filter: userFilter,
      ...queries,
    },
    system,
    is_sql_preview: false,
  }));

  const {
    previewSQL,
    loading: previewSqlLoading,
    error: previewSqlError,
    modalVisible: previewSqlModalVisible,
    hideModal: hidePreviewSqlModal,
    sqlContent: previewSqlContent,
  } = usePreviewSQL({
    fetchPreviewSql: () =>
      previewDashboardChartSql(ChartType.Trend, buildChartParams()),
  });

  const requestData = useMemoizedFn((queries?: Record<string, unknown>) => {
    return calculateDashboardChart(ChartType.Trend, buildChartParams(queries));
  });
  
  const {data, loading, error, run: getData}: any = useRequest(requestData, {
    manual: true,
    onError: (e) => {
      console.log('获取数字趋势卡片信息错误: ', e);
    }
  });

  useEffect(() => {
    !chartsInfoLoading && (filterValues || userFilter?.controls?.length) && getData();
    
  }, [filterValues, chartsInfoLoading, userFilter]);

  const formatValue = useCallback((value: any) => {
    const {column_name: columnName, metrics} = data?.[taskId] || {};
    // 兼容旧图表的 accuracy 和 unit
    let metricInfo = columnName?.find(item => item.name === metrics[0]);
    const { accuracy, unit } = getOldChartDisplayConfig({...metricInfo, column_type: metricInfo.column_date_type}, ChartType.Table);
    metricInfo = {...metricInfo, accuracy, unit};
    return formatDisplayValue({
      value,
      isPercent: false,
      accuracy: metricInfo?.accuracy || 0,
      unit: metricInfo?.column_date_type === 'percent' ? 'percent' : (metricInfo?.unit || 'bmk'),
    });
  }, [data]);
  
  
  const {metricData, curMetricsName, metricKey, changeBubbleData, scopeFilters, userFilterInfo,granularity, trendStatus, isDataError} = useMemo(() => {
    
    try {
  
      // 1.
      if (!data) return {};
      
      const {chart_data, trend_metric={}, user_filter} = data?.[taskId] || {};
      // const metricKey = isEn ? data?.[taskId]?.metrics_en?.[0] : (data?.[taskId]?.metrics?.[0] || data?.[taskId]?.metrics_en?.[0]);
      // 2. 指标名称
      const curMetricsName = data?.[taskId]?.metrics?.[0]?.replace(/(t[0-2]_)/, '') || '';
  
      // 3. 获取scope的筛选器信息
      const scope = getScopeFilters(data?.[taskId]?.filters || []);
      
      /**
       * 4. 计算 日期粒度和范围
       * */
      // 图表中筛选区域的配置
      const chartFilters = JSON.parse(dataMsg?.filter || '{}');
      const chartXColumn = chartFilters?.group_by?.[0]; // X轴配置的 日期字段
      
      // 找到 图表的过滤条件中与X轴配置的相同的日期字段
      // const oldFilterDate = chartFilters?.filters?.filters?.find((item) => item.column === chartXColumn?.column);
      //
      // let info = {
      //   value: oldFilterDate?.value || ['7-day'],
      //   dateType: oldFilterDate?.dateType || 'day',
      // };
      //
      // // 如果报表中有和X轴配置的日期字段相同的筛选器, 则覆盖图表筛选器
      // if (filterValues) {
      //   const newFilterDate = scope?.find(item => chartXColumn?.table_id === item.table_id && item.column === chartXColumn?.column);
      //   if (newFilterDate) info = {value: newFilterDate.value, dateType: newFilterDate.dateType};
      // }
  
      // 从后端返回的 日期筛选器中, 找到与X轴配置的相同的日期字段
      const newFilterDate = scope?.find(item => chartXColumn?.table_id === item.table_id && item.column === chartXColumn?.column);
      const info = {value: newFilterDate?.value ?? ['7-day'], dateType: newFilterDate?.dateType ?? 'day'};
      const {granularity, isSingle: isSingleDate} = getTrendDateInfo(info);
      const isHourDate = newFilterDate?.dateType === 'hour';
      /**
       * 5 针对报表配置中的 isDefaultLastDate判断逻辑
       *
       * 前提:
       *   在图表中 将 create_time日期字段 设置为 X轴 和 Filter, 且设置最近14天;
       *
       * 场景1(对于趋势卡片来说, 存在多个日期筛选器):
       *    在报表中设置了insert_time字段作用于趋势图, 且设置了Default Last Date;
       * 结论:
       *    那么 趋势图 依然按照图表的 create_time 的14天 展示区间值和变化率
       *    (指标值 取 接口中的value)
       *
       * 场景2:
       *   在报表中设置了create_time日期字段, 且设置了Default Last Date;
       * 结论:
       *    那么进入页面时 趋势图 展示单点值 和同环比.
       *   (指标值 取 接口中的 last_date_value)
       */
      
      let isDefaultLastDate = false;
      // 遍历报表筛选器, 找到与图表X轴相同的日期字段
      filterValues.forEach((filter) => {
        const isSameColumn = filter?.column === chartXColumn?.column && filter?.table_id === chartXColumn?.table_id;
        filter.scope_charts?.forEach((scopeChart) => {
          // 查找报表筛选器是否作用于当前趋势卡片
          if (String(scopeChart.task_id) === String(taskId)) {
            // 兼容旧数据保存.
            const isDefault = !!scopeChart.isDefaultDate || !!scopeChart.defaultDate;
            if (isSameColumn) { // 当是报表筛选器和图表的x轴是相同字段时
              isDefaultLastDate = isDefault;
            } else {
              // 查找关联字段是否和 图表X轴的日期字段 相同
              const {column, table_id} = scopeChart?.apply_to_column?.[0] ?? {};
              if (table_id === chartXColumn?.table_id && column === chartXColumn?.column) {
                isDefaultLastDate = isDefault;
              }
            }
          }
        });
      });
      
      // 6. 显示 单点日期 状态, 还是范围日期 状态
      // isFilterInit 为true, 且 isDefaultLastDate为 true 显示单点日期状态
      // isFilterInit 为 false, 且 isSingleDate 为 true时, 显示单点日期状态
      const isSingleState = (isFilterInit && isDefaultLastDate && !isSingleDate) || isSingleDate;
      /**
       * 7. 图表中对趋势图的配置信息 (环比, 同期, 变化率, 趋势图是否显示)
       * 图表中 showTrend, showChange, showSdlw, showDod 为 undefined和true的时候 代表显示.
       */
      const {showTrend=true, showChange=true, showSdlw=true, showDod=true, showStaticTime = false} = JSON.parse(dataMsg?.column_infos || '{}')?.trendStatus || {};
      const trendStatus = {
        showTrend: showTrend && !isHourDate,
        showChange: showChange && !isHourDate && !isSingleState,
        showSdlw: showSdlw && !isHourDate && isSingleState,
        showDod: showDod && !isHourDate && isSingleState,
        showStaticTime,
      };
      
      // 同环比或变化率
      const changData: {dod?: number; sdlw?: number; change?: number;} = {};
      
      // 8. 浮窗中内容数据
      const changeBubbleData = [];
      if (trendStatus.showChange) {
        changeBubbleData.push({
          titleId: 'dbl_common_change',
          thisTime: trend_metric?.change?.this_time,
          lastTime: trend_metric?.change?.last_time,
          thisValue: formatValue(trend_metric?.change?.this_value),
          lastValue: formatValue(trend_metric?.change?.last_value),
        });
        changData.change = trend_metric?.change?.show_value;
      } else {
        switch (granularity) {
          case 'day': {
            changeBubbleData.push(
              {
                titleId: `dbl_common_dod_${granularity}`,
                thisTime: trend_metric?.dod?.this_time,
                lastTime: trend_metric?.dod?.last_time,
                thisValue: formatValue(trend_metric?.dod?.this_value),
                lastValue: formatValue(trend_metric?.dod?.last_value),
              },
              {
                titleId: `dbl_common_sdlw_${granularity}`,
                thisTime: trend_metric?.sdlw?.this_time,
                lastTime: trend_metric?.sdlw?.last_time,
                thisValue: formatValue(trend_metric?.sdlw?.this_value),
                lastValue: formatValue(trend_metric?.sdlw?.last_value),
              },
            );
            changData.dod = trend_metric?.dod?.show_value;
            changData.sdlw = trend_metric?.sdlw?.show_value;
            break;
          }
          case 'week': {
            changeBubbleData.push(
              {
                titleId: `dbl_common_dod_${granularity}`,
                thisTime: trend_metric?.wow?.this_time,
                lastTime: trend_metric?.wow?.last_time,
                thisValue: formatValue(trend_metric?.wow?.this_value),
                lastValue: formatValue(trend_metric?.wow?.last_value),
              },
              {
                titleId: `dbl_common_sdlw_${granularity}`,
                thisTime: trend_metric?.swlm?.this_time,
                lastTime: trend_metric?.swlm?.last_time,
                thisValue: formatValue(trend_metric?.swlm?.this_value),
                lastValue: formatValue(trend_metric?.swlm?.last_value),
              },
            );
            changData.dod = trend_metric?.wow?.show_value;
            changData.sdlw = trend_metric?.swlm?.show_value;
            break;
          }
          case 'month': {
            changeBubbleData.push(
              {
                titleId: `dbl_common_dod_${granularity}`,
                thisTime: trend_metric?.mom?.this_time,
                lastTime: trend_metric?.mom?.last_time,
                thisValue: formatValue(trend_metric?.mom?.this_value),
                lastValue: formatValue(trend_metric?.mom?.last_value),
              },
              {
                titleId: `dbl_common_sdlw_${granularity}`,
                thisTime: trend_metric?.smly?.this_time,
                lastTime: trend_metric?.smly?.last_time,
                thisValue: formatValue(trend_metric?.smly?.this_value),
                lastValue: formatValue(trend_metric?.smly?.last_value),
              },
            );
            changData.dod = trend_metric?.mom?.show_value;
            changData.sdlw = trend_metric?.smly?.show_value;
            break;
          }
      
        }
      }

      return {
        metricData: {
          trendChartData: chart_data?.[0]?.[0]?.metrics || [],
          dod: changData?.dod,
          sdlw: changData?.sdlw,
          change: changData?.change,
          metricValue: formatValue(isSingleState ? trend_metric?.last_date_value : trend_metric?.value) || '-',
          staticTime: (isSingleState ? trend_metric?.last_date : trend_metric?.date) || '-',
        },
        changeBubbleData,
        metricKey: data?.[taskId]?.metrics?.[0],
        // 产品修改需求: 不需要显示聚合名称
        // curMetricsName: curMetricsName + `(${MetricTypes[trend_metric?.aggregation]?.[isEn ? 'text_en' : 'text']})`,
        curMetricsName,
        lastDate: (isFilterInit && isDefaultLastDate) ? `(${trend_metric.last_date})` : undefined,
        scopeFilters: scope,
        granularity,
        trendStatus,
        isSingleState,
        userFilterInfo: user_filter,
      };
      
    } catch (e) {
      
      console.log("报表趋势图卡片数据转换错误: ", e);
      return { isDataError: true};
    }

  }, [data]);
  
  // 图表名称
  // const title = isEn ? dataMsg?.name : (dataMsg?.name_cn || dataMsg?.name);
  
  const jumpToEdit = () => {
    
    const pathId = dataMsg.type === 1 ? 'Action/ExploreAnalysis' : 'Analysis/MultiAnalysis';
    const path = `/${SYSTEM_NAME}/${gamecode}/${pathId}?from=edit&taskId=${dataMsg.task_id}&name=${dataMsg?.name}&nameCn=${dataMsg?.name_cn}`;
    handleOnSubmit?.(path);
  };
  
  const failedMsg = useChartCardError(error);
  
  return (
    <>
    <div ref={cardRootRef} className="dbl-metric-card-root">
      <Card className={'dbl-dashboard-trend-card dbl-metric-card'}>
      <div className={classNames('dbl-card-icon-operation-box', 'dbl-metric-card-operation', !isEdit && 'dbl-card-preview-toolbar')}>
        {
          !isEdit && (!loading && !error) ? (
            <Dropdown
              trigger="hover"
              clickClose={false}
              boxClassName={classNames('dbl-card-drag-operation-dropdown', 'dbl-card-preview-more-menu')}
              button={
                <div className={classNames('dbl-card-drag-operation', 'dbl-card-preview-more-entry', {action: open})}>
                  <SvgIcon type="there-points" />
                </div>
              }
              placementOffset={0}
              open={open}
              appearance="pure"
              placement="bottom-end"
              onOpenChange={(value) => {
                setOpen(value);
              }}
            >
              <List type="option">
                <List.Item className="dbl-card-preview-filter-menu-item">
                  <ShowFilterContentIcon
                    contentMap={{
                      [FILTER_CONTENT_KEY.GENERAL_FILTER]: { filters: scopeFilters},
                      [FILTER_CONTENT_KEY.USER_FILTER]: userFilterInfo,
                    }}
                    isEdit={true}
                    overlayClassName="dbl-card-preview-more-filter-overlay"
                    triggerEndContent={
                      <span className="dbl-card-preview-more-menu-label">
                        {formatMessage({id: 'dbl_filter_tab_current_filter'})}
                      </span>
                    }
                    onVisibleChange={(visible: boolean) => {
                      if (visible) {
                        cardRootRef.current?.classList.add('dbl-show-card-icon');
                      } else {
                        cardRootRef.current?.classList.remove('dbl-show-card-icon');
                      }
                    }}
                  />
                </List.Item>
                {
                  chartExplore && (dataMsg.type === 0) ? (
                    <List.Item
                      className="dbl-card-preview-explore-menu-item"
                      onClick={() => {
                        handleExportClick({
                          filters: data?.[taskId]?.filters,
                          system,
                          gamecode,
                          dataMsg
                        });
                        setOpen(false);
                      }}
                    >
                      <SvgIcon type="explore" />
                      {formatMessage({ id: 'dbl_create_dashboard_icon_explore' })}
                    </List.Item>
                  ) : null
                }
                <List.Item
                  onClick={() => {
                    previewSQL();
                    setOpen(false);
                  }}
                >
                  <SvgIcon type="open-eyes"/>
                  {formatMessage({ id: 'dbl_multi_preview_sql' })}
                </List.Item>
              </List>
            </Dropdown>
          ) : null
        }
        {
          isEdit && <Dropdown
            trigger="hover"
            clickClose={false}
            boxClassName={'dbl-card-drag-operation-dropdown'}
            button={
              <div className={classNames('dbl-card-drag-operation', {action: open})}>
                <SvgIcon type="there-points" />
              </div>
            }
            placementOffset={0}
            open={open}
            appearance="pure"
            placement="bottom-end"
            onOpenChange={(value) => {
              setOpen(value);
            }}
          >
            <List type="option">
              {
                (hasChartPrivilege && dataMsg?.edit) ? (<List.Item
                  onClick={() => {
                    jumpToEdit();
                    setOpen(false);
                  }}
                >
                  <SvgIcon type="pencil-m" />{formatMessage({ id: 'dbl_common_edit' })}
                </List.Item>
                ) : (
                  ''
                )}
              <List.Item
                onClick={() => {
                  previewSQL();
                  setOpen(false);
                }}
              >
                <SvgIcon type="open-eyes"/>
                {formatMessage({ id: 'dbl_multi_preview_sql' })}
              </List.Item>
              <List.Item
                onClick={() => {
                  setOpen(false);
                  deleteCard(dataMsg);
                }}
              >
                <SvgIcon type="trash-can-icon" />{formatMessage({ id: 'dbl_common_delete' })}
              </List.Item>
            </List>
          </Dropdown>
        }
      </div>
      <div className="dbl-metric-card-body">
        <Abnormal
          style={{
            width: '100%',
            height: '100%'
          }}
          isLoading={chartsInfoLoading || loading}
          isError={error || isDataError}
          nodataMsg={formatMessage({ id: "dbl_common_no_filter_data" })}
          failedMsg={failedMsg}
          isNoData={!data?.[taskId]?.chart_data || data?.[taskId]?.chart_data?.length === 0}
          component={(<Card.Body
            className="dbl-metric-card-body-inner"
            title={<>
              <SvgIcon type={'plate'} />
              {curMetricsName}
              {/* <span className={'dbl-dashboard-trend-time'}>{lastDate}</span> */}
            </>}
            operation={<div className='dbl-metric-card-placeholder' />}
          >
            {
              trendStatus?.showStaticTime && metricData?.staticTime ? <div className={'dbl-trend-chart-static-time'}>{metricData?.staticTime}</div> : ''
            }
            <NumberTrendChart
              {...metricData}
              changeBubbleData={changeBubbleData}
              metricNameKey={metricKey}
              // metricName={curMetricsName}
              // metricName={title} // 在报表中 指标名称位置 显示图表名称
              granularity={granularity}
              // 在组件内部不展示staticTime，展示在外部（图表和报表组件展示样式不一致导致）
              trendStatus={{
                ...(trendStatus || {}),
                showStaticTime: false
              }}
              // chartRef={chartRef}
              className={classNames('dashboard', {
                'dbl-trend-chart-body-height': trendStatus?.showStaticTime && metricData?.staticTime
              })}
              formatValue={formatValue}
            />
          </Card.Body>)}
        />
      </div>
    </Card>
    </div>
    <PreviewSQLModal
      visible={previewSqlModalVisible}
      onClose={hidePreviewSqlModal}
      sql={previewSqlContent}
      loading={previewSqlLoading}
      error={previewSqlError}
    />
    </>
  );
};

export default NumberTrendTragCard;
